import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import moment, { Moment } from 'moment-timezone';
import Empty from 'antd/es/empty';
import { Helmet } from 'react-helmet';
import { useQuery } from 'react-query';
import ConfigProvider from 'antd/es/config-provider';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';

import { DailyDeal } from './daily-deal';
import { getColumnsWidth } from 'utils/table';
import { getTeeTimeColumns } from './columns';
import { Text } from 'components/layout/text';
import { WelcomeModal } from './welcome-modal';
import notification from 'antd/es/notification';
import { bookingActions } from 'models/booking';
import { HolidayScreen } from './holiday-screen';
import { MobileTable } from './mobile/mobile-table';
import { WeatherCalendar } from './weather-calendar';
import { renderColumns } from 'components/table/columns';
import { getCourseSelector, isCourseLoadingSelector } from 'models/course/selectors';
import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { queryOptions } from 'modules/api-requests/constants';
import { courseId } from 'modules/api-requests/course/constants';
import { CourseInformation } from 'components/course-information';
import { getConfigLayoutSelector } from 'models/config/selectors';
import { useCourseRates } from 'modules/api-requests/rates/queries';
import { TwoColumnLayout } from 'components/layout/two-column-layout';
import { useTeeTimeList } from 'modules/api-requests/tee-time/queries';
import { BookingErrorModal } from '../column-blocks/booking-error-modal';
import { getWeatherForecastRequest } from 'modules/api-requests/weather';
import { EventNotificationIcon } from './holiday-screen/icons/event-notification-icon';
import { DateFormat, ServerDateFormat, ServerTimeFormat, TimeFormat } from 'utils/constants';
import { EventTypesEnum, getCalendarEventName, hiddenEventTypes } from './holiday-screen/constants';
import {
  HeaderAnchor,
  TeeTimeTable,
  EventsContainer,
  PublicInfoContainer,
  DailyDealContainer,
  BookingListContainer,
  TeeTimeTableContainer,
  WeatherCalendarContainer,
  CustomEventText,
  CustomEventsContainer,
} from './elements';

import type { IWeatherForecastResponse } from 'modules/api-requests/weather/types';
import type { RowClassName } from 'rc-table/lib/interface';
import type { IMetaResponse } from 'modules/api-requests/types';
import { ITableTeeTime } from 'modules/api-requests/tee-time/types';
import { useAvailableRates } from 'pages/booking/hooks/use-filtered-rates';
import { UserWaitlist } from './waitlist/index';
import { CustomBackTop } from './waitlist/elements';

const renderEmpty = () => <Empty description="No tee time" />;

export const BookingListPage = () => {
  const dispatch = useAppDispatch();
  const breakpoints = useBreakpoint();
  const currentCourse = useAppSelector(getCourseSelector);
  const isCourseLoading = useAppSelector(isCourseLoadingSelector);
  const configLayout = useAppSelector(getConfigLayoutSelector);
  const [displayErrorModal, setDisplayErrorModal] = useState(false);
  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);

  const initialCurrentDay = useMemo(() => {
    const prevCurrentDay = moment(localStorage.getItem('currentDay'), DateFormat);

    return !prevCurrentDay.isValid() || prevCurrentDay.isBefore(moment().startOf('day')) ? null : prevCurrentDay;
  }, []);

  const [currentDay, setCurrentDay] = useState<Moment | null | undefined>(initialCurrentDay);

  const currentDate = currentDay ? currentDay.format(ServerDateFormat) : null;

  const {
    data: teeTimeList,
    isFetching,
    error: teeTimeError,
  } = useTeeTimeList(courseId, currentDate, null, false, {
    onError: (error) => {
      if (error === 500) {
        setDisplayErrorModal(true);
      }
    },
  });

  const getRowClassName: RowClassName<any> = useCallback(
    (record) => {
      return record?.freeSlots === 1 && currentCourse?.bestPriceActive ? 'hot' : '';
    },
    [currentCourse]
  );

  const { data: ratesList } = useCourseRates(courseId);
  const filteredRateList = useAvailableRates(teeTimeList?.data, ratesList);

  const teeTimeColumns = useMemo(() => getTeeTimeColumns(filteredRateList), [filteredRateList]);

  const scroll = useMemo(
    () => ({
      scrollToFirstRowOnChange: false,
      x: teeTimeColumns.reduce(getColumnsWidth, 0),
    }),
    [teeTimeColumns]
  );

  useEffect(() => {
    window.gtag &&
      window.gtag('event', 'booking-engine-view', {
        date: currentDay?.format(ServerDateFormat),
        weekDay: currentDay?.format('dddd'),
      });
  }, [currentDay]);

  useEffect(() => {
    dispatch(bookingActions.resetFormFields());
  }, [dispatch]);

  const onWeatherCalendarChange = useCallback((value: string) => {
    localStorage.setItem('currentDay', value);
    setCurrentDay(moment(value, DateFormat));
  }, []);

  const dailyDealTeeTimes = useMemo(() => {
    return teeTimeList?.data?.filter((item: ITableTeeTime) => item.tradeRound?.tradeModel.tradeRounds) ?? [];
  }, [teeTimeList]);

  const hasDailyDeal = Boolean(dailyDealTeeTimes && dailyDealTeeTimes.length);

  const isMobileVersion = !breakpoints.xl;

  const stickyHeader = useMemo(() => {
    if (isMobileVersion) return undefined;
    if (isFetching) return { offsetHeader: 0 };
    if (breakpoints.xxl) return { offsetHeader: 164 + stickyHeaderHeight };
    return { offsetHeader: 112 + stickyHeaderHeight };
  }, [breakpoints, isFetching, isMobileVersion, stickyHeaderHeight]);

  useEffect(() => {
    setTimeout(() => {
      setStickyHeaderHeight(stickyHeaderRef.current?.offsetHeight || 0);
    }, 150);
  }, [breakpoints, isFetching, isMobileVersion]);

  const { data: weatherData } = useQuery({
    queryKey: ['getWeatherForecast', currentCourse?.id],
    queryFn: () => (currentCourse?.id ? getWeatherForecastRequest({ courseId: currentCourse?.id }) : Promise.reject()),
    enabled: Boolean(currentCourse?.id && currentCourse?.weatherActive),
    initialData: {} as IMetaResponse<IWeatherForecastResponse>,
    onError: (error) => notification.error({ message: 'Cannot get weather information', description: String(error) }),
    select: ({ data }) => {
      if (!data) return undefined;

      return data?.map((item) => ({
        date: moment
          .unix(item.dt)
          .tz(currentCourse?.timeZone || moment.tz.guess())
          .format(DateFormat),
        temp: Math.round(item.temp.day),
        description: item.summary,
        icon: item.weather?.[0]?.icon,
      }));
    },
    ...queryOptions,
  });

  const courseDataMoment = useMemo(
    () => (currentCourse?.timeZone ? moment().tz(currentCourse?.timeZone) : null),
    [currentCourse?.timeZone]
  );

  useEffect(() => {
    if (currentCourse && currentCourse.timeZone) {
      moment.tz.setDefault(currentCourse.timeZone);
    }
  }, [currentCourse]);

  useEffect(() => {
    if (courseDataMoment && !Boolean(currentDay)) {
      localStorage.setItem('currentDay', courseDataMoment.format(DateFormat));
      setCurrentDay(courseDataMoment);
    }
  }, [courseDataMoment, currentDay]);

  const filteredTeeTimes = useMemo(() => {
    if (!teeTimeList?.data?.length || !currentCourse?.timeZone || !currentCourse) {
      return teeTimeList?.data;
    }
    const courseDateTime = moment().tz(currentCourse.timeZone);
    return teeTimeList.data.filter((item) => {
      const currentRowDate = moment.tz(
        `${moment(currentDate).format(DateFormat)} ${item.startTime}`,
        `${DateFormat} ${ServerTimeFormat}`,
        currentCourse.timeZone || moment.tz.guess()
      );
      return currentRowDate >= courseDateTime;
    });
  }, [teeTimeList, currentDate, currentCourse]);

  const fullDayEvent =
    (teeTimeList?.meta?.events || [])
      .filter((event) => !hiddenEventTypes.includes(event.type))
      .findIndex((event) => event.isFullDay) !== -1;
  const fullDayEvents = (teeTimeList?.meta?.events || []).filter(
    (event) => !hiddenEventTypes.includes(event.type) && event.isFullDay
  );
  const fullDayEventTitle =
    (teeTimeList?.meta?.events || []).findIndex((event) => event.type === EventTypesEnum.GREENS_AERIFICATION) !== -1
      ? 'Aerification Alert'
      : null;

  const formatTime = (time: string) => moment(time, ServerTimeFormat).format(TimeFormat);
  const courseBlockDependencies = useMemo(
    () => [stickyHeader, isFetching, teeTimeList, hasDailyDeal],
    [stickyHeader, isFetching, teeTimeList, hasDailyDeal]
  );

  const eventsToDisplay = useMemo(() => {
    return (teeTimeList?.meta?.events || []).filter(
      (event) => !hiddenEventTypes.includes(event.type) && event.type !== EventTypesEnum.GREENS_AERIFICATION
    );
  }, [teeTimeList]);

  const customEvents = useMemo(() => {
    return (teeTimeList?.meta?.events || []).filter((event) => event.type === EventTypesEnum.CUSTOM);
  }, [teeTimeList]);

  return (
    <>
      <Helmet title={`${isCourseLoading ? 'Loading...' : currentCourse?.name}`} />
      <WelcomeModal />
      <TwoColumnLayout
        firstColumn={<CourseInformation dependencies={courseBlockDependencies} />}
        secondColumn={
          <BookingListContainer $hasDailyDeal={hasDailyDeal}>
            <HeaderAnchor affix={!isMobileVersion} className="header-anchor">
              <WeatherCalendarContainer>
                <WeatherCalendar value={currentDay} weatherList={weatherData} onChange={onWeatherCalendarChange} />
              </WeatherCalendarContainer>

              <div ref={stickyHeaderRef}>
                {hasDailyDeal && filteredRateList?.length ? (
                  <DailyDealContainer>
                    <DailyDeal teeTimes={dailyDealTeeTimes} rateList={filteredRateList} />
                  </DailyDealContainer>
                ) : (
                  []
                )}

                {customEvents.length ? (
                  <CustomEventsContainer>
                    {customEvents.map((event) => (
                      <CustomEventText key={event.id}>{event.description ? event.description : null}</CustomEventText>
                    ))}
                  </CustomEventsContainer>
                ) : null}
              </div>

              {currentCourse?.isPublicInfoEnabled && currentCourse?.publicInfo ? (
                <PublicInfoContainer>
                  {currentCourse?.publicInfo && currentCourse?.isPublicInfoEnabled ? (
                    <div dangerouslySetInnerHTML={{ __html: currentCourse?.publicInfo }} />
                  ) : null}
                </PublicInfoContainer>
              ) : null}

              {eventsToDisplay.length ? (
                <EventsContainer>
                  <EventNotificationIcon />

                  <div className="container-list">
                    {eventsToDisplay.length ? (
                      <div>
                        <Text>Events this day:</Text>
                        {eventsToDisplay.map((event) => (
                          <Text key={event.id}>
                            <b>{getCalendarEventName(event.type)}</b>
                            {event.description.trim() ? `: ${event.description}.` : ''}
                            {event.isTimeRange && event.startTime && event.endTime
                              ? ` From ${formatTime(event.startTime)} To ${formatTime(event.endTime)}`
                              : ''}
                          </Text>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </EventsContainer>
              ) : null}
            </HeaderAnchor>

            <BookingErrorModal
              onOpenChange={setDisplayErrorModal}
              open={displayErrorModal && Boolean(teeTimeError)}
              title="We are currently unable to process your reservation online."
              error={`Please call the golf shop to make your reservation at ${currentCourse?.contactPhone?.replace(
                '+1',
                ''
              )}`}
            />
            {fullDayEvent ? (
              <HolidayScreen title={fullDayEventTitle} events={fullDayEvents} loading={isFetching} />
            ) : isMobileVersion ? (
              <MobileTable teeTimes={filteredTeeTimes} rates={filteredRateList} loading={isFetching} />
            ) : (
              <TeeTimeTableContainer $shrink={configLayout.shrink}>
                <ConfigProvider renderEmpty={renderEmpty}>
                  <TeeTimeTable
                    rowKey="id"
                    tableLayout="fixed"
                    pagination={false}
                    loading={isFetching}
                    sticky={stickyHeader}
                    dataSource={filteredTeeTimes}
                    rowClassName={getRowClassName}
                    scroll={filteredTeeTimes?.length ? scroll : undefined}
                  >
                    {renderColumns(teeTimeColumns)}
                  </TeeTimeTable>
                </ConfigProvider>
              </TeeTimeTableContainer>
            )}

            <CustomBackTop visible={isMobileVersion} />
            <UserWaitlist />
          </BookingListContainer>
        }
      />
    </>
  );
};

